import React, { useState, useRef, useEffect } from 'react';
import { X, MapPin, Send } from 'lucide-react';

interface LocationInputModalProps {
  isOpen?: boolean; // Add isOpen as optional prop
  onClose: () => void;
  onSend: (location: { lat: string; lon: string; name?: string; address?: string }) => void;
}

const LocationInputModal: React.FC<LocationInputModalProps> = ({ onClose, onSend }) => {
  // 从localStorage恢复上次编辑的内容
  const getStoredLocation = () => {
    try {
      const stored = localStorage.getItem('lastLocationInput');
      return stored ? JSON.parse(stored) : { lat: '', lon: '', name: '', address: '' };
    } catch {
      return { lat: '', lon: '', name: '', address: '' };
    }
  };

  const [location, setLocation] = useState(getStoredLocation());
  const [error, setError] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);

  // 保存编辑内容到localStorage
  useEffect(() => {
    localStorage.setItem('lastLocationInput', JSON.stringify(location));
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleSend = () => {
    if (!location.lat.trim() || !location.lon.trim()) {
      setError('纬度和经度不能为空。');
      return;
    }
    // Basic validation for numeric values
    if (isNaN(Number(location.lat)) || isNaN(Number(location.lon))) {
      setError('纬度和经度必须是数字。');
      return;
    }
    setError('');
    onSend({
      lat: location.lat,
      lon: location.lon,
      name: location.name,
      address: location.address
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-card border border-border rounded-lg shadow-2xl shadow-primary/20 w-full max-w-md flex flex-col animate-slide-in"
      >
        <header className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            发送位置坐标
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X />
          </button>
        </header>
        <main className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">请输入地理位置信息，包括坐标、名称和地址。</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="latitude" className="text-sm font-medium text-muted-foreground">纬度 *</label>
                <input
                  id="latitude"
                  type="text"
                  value={location.lat}
                  onChange={(e) => setLocation({ ...location, lat: e.target.value })}
                  placeholder="例如: 5.6037"
                  className="mt-1 w-full bg-input border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:outline-none"
                />
              </div>
              <div>
                <label htmlFor="longitude" className="text-sm font-medium text-muted-foreground">经度 *</label>
                <input
                  id="longitude"
                  type="text"
                  value={location.lon}
                  onChange={(e) => setLocation({ ...location, lon: e.target.value })}
                  placeholder="例如: -0.1870"
                  className="mt-1 w-full bg-input border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label htmlFor="location-name" className="text-sm font-medium text-muted-foreground">位置名称</label>
              <input
                id="location-name"
                type="text"
                value={location.name}
                onChange={(e) => setLocation({ ...location, name: e.target.value })}
                placeholder="例如: 加纳阿克拉购物中心"
                className="mt-1 w-full bg-input border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="location-address" className="text-sm font-medium text-muted-foreground">详细地址</label>
              <input
                id="location-address"
                type="text"
                value={location.address}
                onChange={(e) => setLocation({ ...location, address: e.target.value })}
                placeholder="例如: Liberation Road, Accra, Ghana"
                className="mt-1 w-full bg-input border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:outline-none"
              />
            </div>
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
        </main>
        <footer className="flex justify-end gap-4 p-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-muted-foreground hover:bg-muted">
            取消
          </button>
          <button onClick={handleSend} className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2">
            <Send className="w-4 h-4" />
            发送
          </button>
        </footer>
      </div>
    </div>
  );
};

export default LocationInputModal;
